import { useTranslation } from "react-i18next";
import { Header } from "@/components/Header";
import { Logo } from "@/components/Logo";
import { CONTACT } from "@/lib/contact";
import { Phone, EnvelopeSimple, InstagramLogo, WhatsappLogo } from "@phosphor-icons/react";
import SkipToContent from "@/components/SkipToContent";

export const Layout = ({ children, noFooter = false }) => {
  return (
    <div className="min-h-screen bg-as-black text-white">
      <SkipToContent />
      <Header />
      <main
        id="main-content"
        tabIndex={-1}
        className="pt-16 focus:outline-none"
        aria-label="Content"
      >
        {children}
      </main>
      {!noFooter && <Footer />}
    </div>
  );
};

const Footer = () => {
  const { t } = useTranslation();
  return (
    <footer
      className="border-t border-as-border bg-as-black mt-24"
      data-testid="footer"
      role="contentinfo"
      aria-label={t("footer.aria_footer")}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid md:grid-cols-3 gap-10">
        <div>
          <Logo size="md" />
          <p className="as-label text-[10px] mt-4 text-as-green">
            {t("footer.tagline")}
          </p>
        </div>
        <div>
          <p className="as-label mb-3">{t("footer.values")}</p>
          <ul className="space-y-2 text-sm text-as-gray">
            <li>{t("footer.value_discipline")}</li>
            <li>{t("footer.value_results")}</li>
            <li>{t("footer.value_health")}</li>
            <li>{t("footer.value_motivation")}</li>
            <li>{t("footer.value_confidence")}</li>
          </ul>
        </div>
        <div>
          <p className="as-label mb-3">{t("footer.contact")}</p>
          <ul className="space-y-3 text-sm">
            <li>
              <a
                href={`https://wa.me/${CONTACT.phoneIntl}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 text-as-gray hover:text-as-green transition-colors"
                data-testid="footer-whatsapp"
              >
                <WhatsappLogo size={18} weight="duotone" color="#39FF14" />
                <span>WhatsApp {CONTACT.phone}</span>
              </a>
            </li>
            <li>
              <a
                href={`tel:+${CONTACT.phoneIntl}`}
                className="flex items-center gap-2 text-as-gray hover:text-as-green transition-colors"
                data-testid="footer-phone"
              >
                <Phone size={18} weight="duotone" color="#39FF14" />
                <span>{CONTACT.phone}</span>
              </a>
            </li>
            <li>
              <a
                href={`mailto:${CONTACT.email}`}
                className="flex items-center gap-2 text-as-gray hover:text-as-green transition-colors break-all"
                data-testid="footer-email"
              >
                <EnvelopeSimple size={18} weight="duotone" color="#39FF14" />
                <span>{CONTACT.email}</span>
              </a>
            </li>
            <li>
              <a
                href={CONTACT.instagramUrl}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 text-as-gray hover:text-as-green transition-colors"
                data-testid="footer-instagram"
              >
                <InstagramLogo size={18} weight="duotone" color="#39FF14" />
                <span>{CONTACT.instagram}</span>
              </a>
            </li>
          </ul>
          <p className="text-xs text-as-gray-dim mt-6">
            © {new Date().getFullYear()} AS TRAINING ·{" "}
            <a href="/privacy" className="text-as-gray hover:text-as-green transition-colors" data-testid="footer-privacy">
              {t("footer.privacy_link")}
            </a>{" "}
            ·{" "}
            <a href="/terms" className="text-as-gray hover:text-as-green transition-colors" data-testid="footer-terms">
              {t("footer.terms_link")}
            </a>{" "}
            ·{" "}
            <a href="/delete-account" className="text-as-gray hover:text-as-green transition-colors" data-testid="footer-delete-account">
              {t("footer.delete_account_link")}
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};
